import { useEffect, useRef, useState } from "react";
import { Plus, UploadSimple, Trash, ChartLineUp, DownloadSimple, Table, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { employee_id: "", client_id: "", service_id: "", log_date: today(), count: 0, remarks: "" };

export default function MISDailyLog() {
  const { activeId } = useCompany();
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState(today().slice(0, 7));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [autoCreate, setAutoCreate] = useState(true);
  const fileRef = useRef(null);
  const wideFileRef = useRef(null);

  // Wide-format import state
  const [wideOpen, setWideOpen] = useState(false);
  const [wideStep, setWideStep] = useState(1); // 1=pick file, 2=map services, 3=commit
  const [wideFile, setWideFile] = useState(null);
  const [wideClientId, setWideClientId] = useState("");
  const [widePreview, setWidePreview] = useState(null);
  const [wideServiceMap, setWideServiceMap] = useState({}); // sub_col -> serviceId or "__new__:Name"
  const [wideBusy, setWideBusy] = useState(false);

  const load = async () => {
    if (!activeId) return;
    const [emps, cs, ss, ents, rep] = await Promise.all([
      api.get("/mis2/employees").catch(() => ({ data: [] })),
      api.get("/mis2/clients", { params: { company_id: activeId } }).catch(() => ({ data: [] })),
      api.get("/mis2/services", { params: { company_id: activeId } }),
      api.get("/mis2/entries", { params: { company_id: activeId, month } }),
      api.get("/mis2/report", { params: { company_id: activeId, month } }),
    ]);
    setEmployees(emps.data || []);
    setClients(cs.data || []);
    setServices(ss.data || []);
    setEntries(ents.data || []);
    setReport(rep.data || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, month]);

  const clientServices = services.filter((s) => s.client_id === form.client_id);

  const save = async () => {
    if (!form.employee_id || !form.client_id || !form.service_id) { toast.error("Pick employee, client and service"); return; }
    if (!form.log_date) { toast.error("Date required"); return; }
    try {
      await api.post("/mis2/entries", { company_id: activeId, ...form, count: Number(form.count || 0) });
      toast.success("Entry saved");
      setOpen(false);
      setForm({ ...empty, log_date: form.log_date, employee_id: form.employee_id });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (entry) => {
    if (!window.confirm(`Delete entry for ${entry.employee_name} on ${entry.log_date}?`)) return;
    await api.delete(`/mis2/entries/${entry.id}`);
    setEntries((curr) => curr.filter((e) => e.id !== entry.id));
    toast.success("Deleted");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_id", activeId);
      fd.append("auto_create", autoCreate ? "true" : "false");
      const { data } = await api.post("/mis2/entries/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      const ac = (data.created_employees || 0) + (data.created_clients || 0) + (data.created_services || 0);
      toast.success(`Imported ${data.inserted} entries${ac ? ` · auto-created ${ac} entit${ac === 1 ? "y" : "ies"}` : ""}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const wideOnFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!clients.length) {
      toast.error("Add at least one client for this company first");
      e.target.value = "";
      return;
    }
    setWideFile(f);
    setWideBusy(true);
    try {
      // Initial parse with empty service_map — backend returns detected_employees + detected_sub_columns
      const fd = new FormData();
      fd.append("file", f);
      fd.append("company_id", activeId);
      // Use any existing client just for the parse; service_map={} gives 0 rows_to_insert but full detection
      fd.append("client_id", clients[0].id);
      fd.append("service_map", JSON.stringify({}));
      fd.append("commit", "false");
      const { data } = await api.post("/mis2/entries/upload-wide", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWidePreview(data);
      const init = {};
      (data.detected_sub_columns || []).forEach((sc) => { init[sc] = `__new__:${sc}`; });
      setWideServiceMap(init);
      setWideStep(2);
    } catch (err) { toast.error(apiErrMsg(err, "Preview failed")); }
    finally { setWideBusy(false); e.target.value = ""; }
  };

  const widePreviewWithMap = async () => {
    if (!wideFile || !wideClientId) { toast.error("Pick a client first"); return; }
    setWideBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", wideFile);
      fd.append("company_id", activeId);
      fd.append("client_id", wideClientId);
      fd.append("service_map", JSON.stringify(wideServiceMap));
      fd.append("commit", "false");
      const { data } = await api.post("/mis2/entries/upload-wide", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWidePreview(data);
      setWideStep(3);
    } catch (err) { toast.error(apiErrMsg(err, "Preview failed")); }
    finally { setWideBusy(false); }
  };

  const wideCommit = async () => {
    if (!wideFile || !wideClientId) return;
    setWideBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", wideFile);
      fd.append("company_id", activeId);
      fd.append("client_id", wideClientId);
      fd.append("service_map", JSON.stringify(wideServiceMap));
      fd.append("auto_create_employees", "true");
      fd.append("commit", "true");
      const { data } = await api.post("/mis2/entries/upload-wide", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} entries · created ${data.created_employees} emp · ${data.created_services} svc`);
      setWideOpen(false);
      setWideFile(null); setWidePreview(null); setWideServiceMap({}); setWideClientId(""); setWideStep(1);
      load();
    } catch (err) { toast.error(apiErrMsg(err, "Commit failed")); }
    finally { setWideBusy(false); }
  };


  const downloadTemplate = async () => {
    if (!activeId) { toast.error("Pick a company first"); return; }
    try {
      const res = await api.get("/mis2/entries/template", {
        params: { company_id: activeId },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily_mis_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill it & upload back");
    } catch (e) { toast.error(e?.response?.data?.detail || "Template download failed"); }
  };

  return (
    <Layout
      title="Daily MIS Log"
      subtitle={`${entries.length} entries in ${month}`}
      actions={
        <>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={upload} className="hidden" />
          <label className="hidden md:inline-flex items-center gap-1.5 text-[10px] font-mono-shelv uppercase tracking-widest text-[#4A4D57] cursor-pointer mr-1" title="When ON, missing employees / clients / services are created on the fly from the uploaded file">
            <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} className="accent-[#D90429]" data-testid="mis-auto-create-toggle"/>
            Auto-create missing
          </label>
          <button onClick={downloadTemplate} className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-[#0D9488] text-[#0D9488] text-xs md:text-sm font-semibold hover:bg-[#0D9488] hover:text-white" data-testid="download-template-mis">
            <DownloadSimple size={14} weight="bold" /> Download Template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-black/15 text-xs md:text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50" data-testid="bulk-upload-mis">
            <UploadSimple size={14} weight="bold" /> {uploading ? "Importing…" : "Bulk Upload"}
          </button>
          <button onClick={() => { setWideOpen(true); setWideStep(1); setWideFile(null); setWidePreview(null); setWideServiceMap({}); setWideClientId(""); }} className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-[#F4A261] text-[#F4A261] text-xs md:text-sm font-semibold hover:bg-[#F4A261] hover:text-white" data-testid="wide-upload-mis" title="Import wide-format pivot Excel (dates × employees × sub-columns)">
            <Table size={14} weight="bold" /> Wide Import
          </button>
          <button onClick={downloadTemplate} className="md:hidden p-2 border border-[#0D9488] text-[#0D9488]" aria-label="Download template"><DownloadSimple size={14} weight="bold" /></button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="md:hidden p-2 border border-black/15 disabled:opacity-50" aria-label="Bulk upload"><UploadSimple size={14} weight="bold" /></button>
          <button onClick={() => { setForm({ ...empty, log_date: today() }); setOpen(true); }} className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 md:py-2.5 bg-[#D90429] text-white text-xs md:text-sm font-semibold hover:bg-[#EF233C]" data-testid="add-entry-button">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Add Entry</span><span className="sm:hidden">Add</span>
          </button>
        </>
      }
    >
      {uploadResult && (
        <div className="mb-4 border-l-4 border-[#D90429] bg-[#FFE5E9]/40 px-4 py-3 text-sm" data-testid="upload-result">
          <div className="font-bold text-[#0A0A0A]">Import complete · {uploadResult.inserted} added{uploadResult.skipped_blank > 0 && `, ${uploadResult.skipped_blank} blanks skipped`}</div>
          {(uploadResult.created_employees > 0 || uploadResult.created_clients > 0 || uploadResult.created_services > 0) && (
            <div className="text-xs text-[#0D9488] mt-1 font-semibold" data-testid="upload-autocreate-summary">
              ✓ Auto-created:
              {uploadResult.created_employees > 0 && <> {uploadResult.created_employees} employee(s) ·</>}
              {uploadResult.created_clients > 0 && <> {uploadResult.created_clients} client(s) ·</>}
              {uploadResult.created_services > 0 && <> {uploadResult.created_services} service(s) — <span className="text-[#D90429]">set rates in Services &amp; Rates so revenue maths is correct</span></>}
            </div>
          )}
          {uploadResult.errors?.length > 0 && (
            <ul className="text-xs text-[#D90429] mt-1 max-h-32 overflow-y-auto">{uploadResult.errors.map((er, i) => <li key={`err-${i}`}>· {er}</li>)}</ul>
          )}
          <button onClick={() => setUploadResult(null)} className="text-xs text-[#717680] mt-1">Dismiss</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full sm:w-48 rounded-none" data-testid="month-select" />
      </div>

      {/* Monthly rollup */}
      {report && report.rows.length > 0 && (
        <div className="mb-6 border border-black/10 bg-white overflow-x-auto" data-testid="mis-report">
          <div className="px-4 py-3 border-b border-black/10 flex items-center gap-2 bg-[#F5F6F8]">
            <ChartLineUp size={16} weight="bold" className="text-[#D90429]" />
            <div className="font-display font-bold tracking-tight">Monthly rollup · {report.month}</div>
            {isAdmin && report.total_revenue != null && (
              <div className="ml-auto font-mono-shelv text-sm" data-testid="total-revenue">Revenue: <b>₹{Number(report.total_revenue).toLocaleString("en-IN")}</b></div>
            )}
          </div>
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv bg-[#FAFBFC]">
              <tr>
                <th className="px-4 py-2">Client</th><th className="px-4 py-2">Service</th><th className="px-4 py-2 text-right">Count</th>
                {isAdmin && <><th className="px-4 py-2 text-right">Rate</th><th className="px-4 py-2 text-right">Revenue</th></>}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={`${r.client_id}-${r.service_id}`} className="border-t border-black/5">
                  <td className="px-4 py-2">{r.client_name}</td>
                  <td className="px-4 py-2">{r.service_name}</td>
                  <td className="px-4 py-2 text-right font-mono-shelv">{Number(r.count).toLocaleString("en-IN")} <span className="text-[10px] text-[#717680]">{r.unit}</span></td>
                  {isAdmin && <>
                    <td className="px-4 py-2 text-right font-mono-shelv">₹{Number(r.rate || 0).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv font-bold">₹{Number(r.revenue || 0).toLocaleString("en-IN")}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Daily entries */}
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="entries-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>{["Date", "Employee", "Client", "Service", "Count", "Remarks", ""].map((h) => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#717680]">No entries in {month}. Add one above.</td></tr>}
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-black/5" data-testid={`entry-row-${e.id}`}>
                <td className="px-4 py-2 font-mono-shelv text-xs">{e.log_date}</td>
                <td className="px-4 py-2">{e.employee_name}</td>
                <td className="px-4 py-2">{e.client_name}</td>
                <td className="px-4 py-2">{e.service_name}</td>
                <td className="px-4 py-2 font-mono-shelv">{Number(e.count).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-xs text-[#717680] max-w-xs truncate">{e.remarks}</td>
                <td className="px-4 py-2 text-right"><button onClick={() => remove(e)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`del-entry-${e.id}`}><Trash size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>New daily entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Date *</Label><Input type="date" value={form.log_date} onChange={(e) => setForm((f) => ({ ...f, log_date: e.target.value }))} className="rounded-none" data-testid="entry-date" /></div>
            <div><Label className="text-xs">Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger className="rounded-none" data-testid="entry-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v, service_id: "" }))}>
                <SelectTrigger className="rounded-none" data-testid="entry-client"><SelectValue placeholder="Pick client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Service *</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))} disabled={!form.client_id}>
                <SelectTrigger className="rounded-none" data-testid="entry-service"><SelectValue placeholder={form.client_id ? "Pick service" : "Pick client first"} /></SelectTrigger>
                <SelectContent>
                  {clientServices.length === 0 && <div className="px-3 py-2 text-xs text-[#717680]">No services for this client yet.</div>}
                  {clientServices.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.unit})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Count *</Label><Input type="number" step="1" min="0" value={form.count} onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))} className="rounded-none" data-testid="entry-count" /></div>
            <div><Label className="text-xs">Remarks</Label><Input value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} className="rounded-none" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]" data-testid="entry-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wide-format Import dialog */}
      <input ref={wideFileRef} type="file" accept=".xlsx,.xls" onChange={wideOnFilePick} className="hidden" />
      <Dialog open={wideOpen} onOpenChange={setWideOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Wide-Format Excel Import
              <span className="text-xs text-[#717680] font-normal ml-2">Step {wideStep} of 3</span>
            </DialogTitle>
          </DialogHeader>

          {wideStep === 1 && (
            <div className="space-y-4 py-6">
              <p className="text-sm text-[#4A4D57]">
                Upload a pivot-format Excel where <b>dates are rows</b>, <b>employees are columns</b>, and each employee has sub-columns like &quot;Entry Docket&quot; &amp; &quot;Page&quot;. Weekend cells with &quot;SATURDAY&quot;, &quot;SUNDAY&quot;, or &quot;Resigned&quot; markers are auto-skipped.
              </p>
              <button onClick={() => wideFileRef.current?.click()} disabled={wideBusy} className="w-full py-6 border-2 border-dashed border-[#F4A261] hover:bg-[#F4A261]/5 text-[#F4A261] font-semibold text-sm disabled:opacity-50" data-testid="wide-pick-file">
                {wideBusy ? "Parsing…" : "📊 Pick Excel file"}
              </button>
            </div>
          )}

          {wideStep === 2 && widePreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <StatBox l="Sheet" v={widePreview.sheet}/>
                <StatBox l="Employees detected" v={widePreview.detected_employees?.length || 0}/>
                <StatBox l="Dates detected" v={widePreview.dates?.length || 0}/>
              </div>
              <div>
                <Label className="text-xs">Client (all rows will be attributed to this client) *</Label>
                <Select value={wideClientId} onValueChange={setWideClientId}>
                  <SelectTrigger className="rounded-none" data-testid="wide-client-select"><SelectValue placeholder="Pick client"/></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="border border-black/10 p-4">
                <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-3">Map sub-columns → MIS Services</div>
                <div className="space-y-2">
                  {(widePreview.detected_sub_columns || []).map((sc) => {
                    const filteredSvcs = wideClientId ? services.filter((s) => s.client_id === wideClientId) : [];
                    const cur = wideServiceMap[sc] || `__new__:${sc}`;
                    return (
                      <div key={sc} className="flex items-center gap-3">
                        <div className="w-40 font-semibold text-sm">{sc}</div>
                        <ArrowRight size={14} className="text-[#717680]"/>
                        <Select value={cur} onValueChange={(v) => setWideServiceMap((m) => ({ ...m, [sc]: v }))}>
                          <SelectTrigger className="rounded-none flex-1" data-testid={`wide-svc-${sc}`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={`__new__:${sc}`}>+ Create new service &quot;{sc}&quot;</SelectItem>
                            {filteredSvcs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                {!wideClientId && <p className="text-[11px] text-[#D90429] mt-2">Pick a client above to see its existing services.</p>}
              </div>
              <div>
                <Label className="text-xs">Employees found in file:</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {(widePreview.detected_employees || []).map((e) => (
                    <span key={e} className="px-2 py-0.5 bg-[#F5F6F8] text-[11px] font-mono-shelv">{e}</span>
                  ))}
                </div>
                <p className="text-[11px] text-[#717680] mt-1.5">Missing ones will be auto-created as active employees.</p>
              </div>
              <DialogFooter>
                <button onClick={() => setWideOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
                <button onClick={widePreviewWithMap} disabled={!wideClientId || wideBusy} className="px-4 py-2 bg-[#F4A261] text-white text-sm font-semibold hover:bg-[#EF9748] disabled:opacity-50" data-testid="wide-preview-btn">
                  {wideBusy ? "Previewing…" : "Preview & confirm →"}
                </button>
              </DialogFooter>
            </div>
          )}

          {wideStep === 3 && widePreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <StatBox l="Rows to insert" v={widePreview.rows_to_insert || 0} c="#D90429"/>
                <StatBox l="Dates covered" v={widePreview.dates?.length || 0}/>
                <StatBox l="Employees to auto-create" v={widePreview.employees_missing?.length || 0} c={(widePreview.employees_missing?.length || 0) > 0 ? "#F4A261" : "#0D9488"}/>
                <StatBox l="Services to create" v={widePreview.services_to_create?.length || 0} c={(widePreview.services_to_create?.length || 0) > 0 ? "#F4A261" : "#0D9488"}/>
              </div>
              {widePreview.services_to_create?.length > 0 && (
                <div className="border border-[#F4A261]/40 bg-[#F4A261]/10 p-3 text-[12px]">
                  New services will be created (rate=0, set later in Services &amp; Rates):
                  <ul className="mt-1 list-disc pl-5">{widePreview.services_to_create.map((s, i) => <li key={i}>{s.service_name} (from column &quot;{s.sub_column}&quot;)</li>)}</ul>
                </div>
              )}
              {widePreview.sample?.length > 0 && (
                <div className="border border-black/10 overflow-x-auto">
                  <div className="px-3 py-2 bg-[#F5F6F8] text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680]">Preview (first 20 rows)</div>
                  <table className="w-full text-[12px]">
                    <thead className="bg-[#FAFBFC] text-left">
                      <tr>{["Date","Employee","Service (from)","Count"].map(h => <th key={h} className="px-3 py-1.5 font-semibold">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {widePreview.sample.map((r, i) => (
                        <tr key={i} className="border-t border-black/5">
                          <td className="px-3 py-1.5 font-mono-shelv">{r.log_date}</td>
                          <td className="px-3 py-1.5">{r.employee}</td>
                          <td className="px-3 py-1.5">{r.sub_column}</td>
                          <td className="px-3 py-1.5 font-mono-shelv font-bold">{Number(r.count).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <DialogFooter>
                <button onClick={() => setWideStep(2)} className="px-4 py-2 border border-black/15 text-sm">← Back</button>
                <button onClick={wideCommit} disabled={wideBusy || (widePreview.rows_to_insert || 0) === 0} className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="wide-commit-btn">
                  {wideBusy ? "Importing…" : `Import ${widePreview.rows_to_insert} rows →`}
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function StatBox({ l, v, c = "#0A0A0A" }) {
  return (
    <div className="border border-black/10 p-3">
      <div className="font-mono-shelv text-[10px] tracking-widest uppercase text-[#717680]">{l}</div>
      <div className="font-display text-xl font-black tracking-tighter mt-1" style={{ color: c }}>{typeof v === "number" ? v.toLocaleString("en-IN") : v}</div>
    </div>
  );
}
